// ============================================
// ImportProductsModal
// Import sản phẩm từ CSV/XLSX (sheet 2 — mục #13/#21)
// ============================================
//
// POST /products/import/ — multipart, upsert theo `sku`.
//
// Thiết kế xoay quanh ba đặc tính của hợp đồng:
//
// 1. Ô TRỐNG = GIỮ NGUYÊN, không xoá. Đây là thứ khiến file hai cột
//    `sku,price` trở thành công cụ cập nhật giá hàng loạt — nói rõ trên màn
//    hình, vì mặc định người dùng sẽ sợ file thiếu cột sẽ xoá mất dữ liệu.
// 2. `dry_run` chạy thử KHÔNG ghi gì. Màn này BẮT BUỘC chạy thử trước, và chỉ
//    mở nút ghi thật sau khi đã thử — import 2000 dòng sai vào danh mục sản
//    phẩm thật là thứ không có nút hoàn tác.
// 3. Lỗi TỪNG DÒNG về kèm HTTP 200 trong `failed[]`; 400 chỉ dành cho lỗi mức
//    file. Hai loại lỗi này hiện ở hai chỗ khác nhau trên màn hình.
//
// `row` trong `failed[]` đánh số theo Excel (tính cả dòng tiêu đề), nên hiện
// nguyên số đó để người dùng mở file lên là tới đúng dòng.

import { useRef, useState } from 'react';
import type { ProductImportResponse } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  useImportProducts,
  PRODUCT_IMPORT_MAX_ROWS,
  PRODUCT_IMPORT_MAX_BYTES,
} from '../../hooks';

/** Cột BE nhận. `sku` bắt buộc, còn lại tuỳ chọn. Cột lạ → BE từ chối cả file. */
const COLUMNS = [
  'sku',
  'name',
  'price',
  'cost_price',
  'tax_rate',
  'barcode',
  'unit',
  'brand',
  'status',
  'category',
];

interface ImportProductsModalProps {
  onClose: () => void;
}

export function ImportProductsModal({ onClose }: ImportProductsModalProps) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const { mutate: runImport, isPending } = useImportProducts();

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProductImportResponse | null>(null);
  /** Đã chạy thử cho ĐÚNG file này chưa — đổi file thì phải thử lại. */
  const [dryRunDone, setDryRunDone] = useState(false);

  const pickFile = (next: File | null) => {
    setFile(next);
    setError(null);
    setResult(null);
    setDryRunDone(false);
  };

  const submit = (dryRun: boolean) => {
    setError(null);

    if (!file) {
      setError(t('importErrorNoFile'));
      return;
    }
    // Chặn trước ở client cho thông báo rõ ràng; BE vẫn là nơi chốt.
    if (file.size > PRODUCT_IMPORT_MAX_BYTES) {
      setError(t('importErrorTooBig'));
      return;
    }

    runImport(
      { file, dryRun },
      {
        onSuccess: (res) => {
          setResult(res);
          const failed = res.failed?.length ?? 0;
          if (dryRun) {
            // Chỉ mở đường ghi thật khi chạy thử KHÔNG còn dòng hỏng.
            setDryRunDone(failed === 0);
          } else if (failed === 0) {
            onClose();
          }
        },
        onError: (err: unknown) => {
          // Nhánh này là lỗi mức FILE (400) hoặc lỗi mạng — không phải dòng lẻ.
          setError(err instanceof Error ? err.message : t('importFailed'));
          setResult(null);
          setDryRunDone(false);
        },
      }
    );
  };

  const failedRows = result?.failed ?? [];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('importTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{t('importSubtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              {t('importFileLabel')}
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700"
            />
            <p className="mt-1 text-xs text-slate-500">
              {t('importLimits')
                .replace('{rows}', String(PRODUCT_IMPORT_MAX_ROWS))
                .replace('{mb}', String(PRODUCT_IMPORT_MAX_BYTES / 1024 / 1024))}
            </p>
          </div>

          <div className="rounded-lg bg-slate-50 px-3 py-3 text-xs text-slate-600">
            <p className="font-semibold text-slate-700">{t('importColumnsTitle')}</p>
            <p className="mt-1 font-mono">{COLUMNS.join(', ')}</p>
            <p className="mt-2">{t('importUpsertHint')}</p>
            <p className="mt-1">{t('importUnknownColumnHint')}</p>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          {result && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('importTotal')}
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{result.summary?.total ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('importCreated')}
                  </p>
                  <p className="mt-1 text-sm font-bold text-emerald-600">
                    {result.summary?.created ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('importUpdated')}
                  </p>
                  <p className="mt-1 text-sm font-bold text-blue-600">
                    {result.summary?.updated ?? 0}
                  </p>
                </div>
              </div>

              {failedRows.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-red-200">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 font-bold uppercase tracking-wider text-red-700">
                      <tr>
                        <th className="w-16 px-2 py-2 text-left">{t('importRow')}</th>
                        <th className="w-32 px-2 py-2 text-left">SKU</th>
                        <th className="px-2 py-2 text-left">{t('importError')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {failedRows.map((r, i) => (
                        <tr key={`${r.row ?? i}-${r.sku ?? i}`}>
                          <td className="px-2 py-1.5 font-mono text-slate-500">{r.row ?? '—'}</td>
                          <td className="px-2 py-1.5 font-mono">{r.sku ?? '—'}</td>
                          <td className="px-2 py-1.5 text-red-700">{r.error ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                dryRunDone && (
                  <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {t('importDryRunClean')}
                  </p>
                )
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-6 py-4">
          <span className="text-xs text-slate-500">
            {dryRunDone ? t('importReadyToWrite') : t('importMustDryRun')}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isPending}
              className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
            >
              {t('cancel')}
            </button>
            <button
              onClick={() => submit(true)}
              disabled={isPending || !file}
              className="rounded-lg bg-slate-800 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-900 disabled:opacity-50"
            >
              {isPending ? t('loading') : t('importDryRun')}
            </button>
            <button
              onClick={() => submit(false)}
              disabled={isPending || !dryRunDone}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {t('importSubmit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
